/**
 * Collision is world-space AABB on the player's *visual* X, not a discrete
 * lane index. Swiping off an obstacle in front of you clears X before Z
 * overlaps — that's the dodge. Swept Z so a fast frame can't tunnel.
 */
import { LANES, PLAYER_Z } from "./constants";

export type HazardKind = "box" | "barrier" | "rival" | "truck" | "hole";

export const PLAYER_HALF_X = 0.34;
export const PLAYER_HALF_Z = 0.62;
/** Matches LaneWorld visual lerp so tests and game share the same motion. */
export const LANE_LERP = 26;
/** Collision box vs visible mesh — slightly smaller so edges feel fair. */
export const VISUAL_FIT = 0.92;

export type Aabb = { halfX: number; halfZ: number };

/** Defaults until a mesh is measured. Sized close to real crates/cars. */
export const HAZARD_BOX: Record<HazardKind, Aabb> = {
  rival: { halfX: 0.4, halfZ: 0.7 },
  truck: { halfX: 0.52, halfZ: 1.05 },
  barrier: { halfX: 0.58, halfZ: 0.18 },
  box: { halfX: 0.46, halfZ: 0.46 },
  hole: { halfX: 0.52, halfZ: 0.48 },
};

/** Jump clears ground clutter and cars. Trucks are too tall. */
export function jumpClears(kind: HazardKind): boolean {
  return kind !== "truck";
}

export const PICKUP_HALF_X = 0.48;
export const PICKUP_HALF_Z = 0.62;

export function stepLaneX(x: number, target: number, dt: number): number {
  return x + (target - x) * (1 - Math.exp(-LANE_LERP * dt));
}

export function hitsOnZ(kind: HazardKind, zNow: number, zPrev: number): boolean {
  const box = HAZARD_BOX[kind];
  return hitsOnZExtent(zNow, zPrev, PLAYER_HALF_Z, box.halfZ);
}

export function hitsOnZExtent(
  zNow: number,
  zPrev: number,
  playerHalfZ: number,
  hazardHalfZ: number,
): boolean {
  const front = playerHalfZ + hazardHalfZ;
  const rear = hazardHalfZ;
  const lo = PLAYER_Z - front;
  const hi = PLAYER_Z + rear;
  if (zNow > lo && zNow < hi) return true;
  const a = Math.min(zPrev, zNow);
  const b = Math.max(zPrev, zNow);
  return b > lo && a < hi;
}

export function playerHitsHazard(
  playerX: number,
  h: {
    lane: number;
    z: number;
    prevZ: number;
    kind: HazardKind;
    halfX?: number;
    halfZ?: number;
  },
  playerHalfX = PLAYER_HALF_X,
  playerHalfZ = PLAYER_HALF_Z,
): boolean {
  const box = HAZARD_BOX[h.kind];
  const hhx = h.halfX ?? box.halfX;
  const hhz = h.halfZ ?? box.halfZ;
  if (Math.abs(playerX - LANES[h.lane]) >= playerHalfX + hhx) return false;
  return hitsOnZExtent(h.z, h.prevZ, playerHalfZ, hhz);
}

export function hitsPickup(
  playerX: number,
  px: number,
  pz: number,
  magnet: boolean,
): boolean {
  const halfX = magnet ? 0.85 : PICKUP_HALF_X;
  return Math.abs(playerX - px) < halfX && Math.abs(pz - PLAYER_Z) < PICKUP_HALF_Z;
}

/** Keep solid hazards in a lane from overlapping (rivals brake behind crates). */
export function stackLane(
  items: { kind: HazardKind; z: number; halfZ?: number }[],
  gap = 0.4,
): void {
  const solids = items.filter((h) => h.kind !== "hole");
  solids.sort((a, b) => a.z - b.z);
  for (let i = solids.length - 1; i >= 1; i--) {
    const ahead = solids[i]!;
    const behind = solids[i - 1]!;
    const ah = ahead.halfZ ?? HAZARD_BOX[ahead.kind].halfZ;
    const bh = behind.halfZ ?? HAZARD_BOX[behind.kind].halfZ;
    const maxZ = ahead.z - ah - bh - gap;
    if (behind.z > maxZ) behind.z = maxZ;
  }
}

const HITBOX_KEY = "lanedash.hitbox";

export function hitboxesEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("hitbox") === "1") return true;
    if (q.get("hitbox") === "0") return false;
    return window.localStorage.getItem(HITBOX_KEY) === "1";
  } catch {
    return false;
  }
}

export function setHitboxesEnabled(on: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(HITBOX_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}
